/**
 * The dues banner on My Bib and Payments.
 *
 * The rule that matters: the CTA is never PRESSABLE unless money is actually
 * owed. A member who has paid can no longer pay twice. The decision lives in
 * `payableUi()` in src/data and is never re-derived here, because it was a
 * real bug in an earlier build. SPEC.md §4.
 *
 * What changed on 16 Aug 2026 (owner's direction) is only visibility: the
 * button now stays on screen while a membership is comfortably paid, disabled
 * and carrying the date it unlocks, instead of vanishing. Hiding it entirely
 * left members hunting for a control that was not there yet.
 */

import { duesState, formatDate, memberFee, payableUi } from '../data';
import { useFees } from '../context/ClubContext';
import { Button } from './ui';

// variant: 'home' | 'payments' — Payments shows a little more detail than My Bib.

export function DuesBar({ member, onPay, variant = 'home' }) {
  const dues = duesState(member);
  // The CLUB's amount for this member — joining if never paid, renewal after.
  const fee = memberFee(useFees(), member);
  const isAlert = dues.key === 'lapsed' || dues.key === 'pending';
  const isSoon = dues.key === 'soon';
  const pay = payableUi(member);

  // alert → red, soon → neutral (it is not an emergency yet), otherwise green
  const toneClass = isAlert
    ? 'border-flag-100 bg-flag-50'
    : isSoon
      ? 'border-line bg-white'
      : 'border-sea-300 bg-sea-50';

  const heading = () => {
    if (member.tier === 'life') return variant === 'payments' ? 'Life member' : 'Membership active';
    if (isAlert) return variant === 'payments' ? 'Payment due' : 'Membership due';
    if (isSoon) return 'Renewal coming up';
    return 'Membership active';
  };

  const detail = () => {
    if (member.tier === 'life') {
      return variant === 'payments' ? 'Nothing to pay — ever.' : 'Life member — nothing to pay.';
    }
    if (dues.key === 'pending') return `₹${fee} to join`;
    if (dues.key === 'paid' && variant === 'payments') {
      return `Valid until ${formatDate(member.duesPaidTill)} — nothing due`;
    }
    return dues.label;
  };

  return (
    <div
      className={`mb-3.5 flex flex-wrap items-center justify-between gap-3.5 border p-4 ${toneClass}`}
    >
      <div>
        <div className="font-market text-[17px] font-bold tracking-[-0.015em]">
          {heading()}
        </div>
        <div className="mt-1 font-market text-[10.5px] text-copy-muted">{detail()}</div>
      </div>
      {pay.show && (
        <span className="flex flex-col items-end gap-1">
          <Button tone="p" onClick={onPay} disabled={!pay.enabled}>
            {isAlert ? 'Pay' : 'Renew'} ₹{fee}
            {variant === 'home' ? ' now' : ''}
          </Button>
          {/* A disabled control must say why, or it reads as broken. */}
          {pay.hint && (
            <span className="font-market text-[10px] text-copy-label">{pay.hint}</span>
          )}
        </span>
      )}
    </div>
  );
}
